// 🔴 CHANTIER — dictée vocale du relevé (⛔ importée d'aucun écran DAL).
//
// 🔑 N°963 — DEUX chemins, le PRINCIPAL ⛔ ne dépend plus du navigateur :
//   · AUDIO (principal) : MediaRecorder capture le micro → l'audio MONTE au serveur
//     → ElevenLabs Scribe transcrit → `interpretation.proposer_lignes`. Marche partout
//     où `getUserMedia`+`MediaRecorder` existent (Chrome/Firefox/Safari mobiles).
//   · PAROLE→TEXTE NAVIGATEUR (repli) : `SpeechRecognition`, conservé UNIQUEMENT comme
//     secours, ⛔ jamais le seul moyen (il « existe et ne rend rien » sur mobile).
// 🔑 La dictée ⛔ n'est qu'un moyen de REMPLIR le texte, qui reste éditable au clavier.
// ⚠️ ⛔ AUCUNE panne muette : toute erreur (permission refusée, enregistrement, upload)
//    est REMONTÉE dans `erreur` ; l'humain voit, et peut taper.
import { useCallback, useEffect, useRef, useState } from "react";

const DUREE_MAX_MS = 120000; // garde-fou : coupe un enregistrement oublié (= serveur).

function moteurVocal() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function audioDisponible() {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  return Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
}

// onTexte(str)  : chemin repli SpeechRecognition (texte reconnu côté navigateur).
// onAudio(blob, dureeS) -> Promise : chemin principal (l'appelant POSTe l'audio au
//   serveur et affiche la proposition). `useDictee` l'attend pour piloter `occupe`.
export function useDictee(onTexte, { onAudio } = {}) {
  const [ecoute, setEcoute] = useState(false);      // SpeechRecognition en cours
  const [enregistre, setEnregistre] = useState(false); // MediaRecorder en cours
  const [occupe, setOccupe] = useState(false);      // upload/transcription en cours
  const [erreur, setErreur] = useState("");
  const rec = useRef(null);                          // SpeechRecognition
  const mediaRec = useRef(null);                     // MediaRecorder
  const chunks = useRef([]);
  const flux = useRef(null);                         // MediaStream (pour couper le micro)
  const minuteur = useRef(null);
  const debut = useRef(0);

  const disponible = Boolean(moteurVocal());
  const audioDispo = audioDisponible();

  const _couperFlux = useCallback(() => {
    try { flux.current?.getTracks().forEach((t) => t.stop()); } catch { /* déjà coupé */ }
    flux.current = null;
    if (minuteur.current) { clearTimeout(minuteur.current); minuteur.current = null; }
  }, []);

  useEffect(() => () => {
    try { rec.current?.abort(); } catch { /* déjà arrêté */ }
    try { if (mediaRec.current && mediaRec.current.state !== "inactive") mediaRec.current.stop(); } catch { /* idem */ }
    _couperFlux();
  }, [_couperFlux]);

  // ── Chemin PRINCIPAL : enregistrement audio ────────────────────────────────
  const demarrerAudio = useCallback(async () => {
    if (!audioDispo) { setErreur("Micro indisponible sur cet appareil."); return; }
    if (enregistre || occupe) return;
    setErreur("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      flux.current = stream;
      chunks.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.current.push(ev.data); };
      mr.onerror = () => { setErreur("Enregistrement interrompu."); setEnregistre(false); _couperFlux(); };
      mr.onstop = async () => {
        _couperFlux();
        setEnregistre(false);
        const type = mr.mimeType || (chunks.current[0]?.type) || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        chunks.current = [];
        if (!blob.size) { setErreur("Aucun son capté — réessayez plus près du micro."); return; }
        if (!onAudio) return;
        const dureeS = debut.current ? (Date.now() - debut.current) / 1000 : undefined;
        setOccupe(true);
        try { await onAudio(blob, dureeS); }
        catch (e) { setErreur(e?.message || "Échec de la transcription."); }
        finally { setOccupe(false); }
      };
      mediaRec.current = mr;
      debut.current = Date.now();
      mr.start();
      setEnregistre(true);
      minuteur.current = setTimeout(() => {
        try { if (mr.state !== "inactive") mr.stop(); } catch { /* déjà arrêté */ }
      }, DUREE_MAX_MS);
    } catch (e) {
      // getUserMedia refusé / indisponible — ⛔ jamais muet.
      setErreur(e?.name === "NotAllowedError"
        ? "Micro refusé : autorisez l'accès au micro, ou tapez le texte."
        : "Micro inaccessible — tapez le texte.");
      _couperFlux();
      setEnregistre(false);
    }
  }, [audioDispo, enregistre, occupe, onAudio, _couperFlux]);

  const arreterAudio = useCallback(() => {
    try { if (mediaRec.current && mediaRec.current.state !== "inactive") mediaRec.current.stop(); }
    catch { setEnregistre(false); _couperFlux(); }
  }, [_couperFlux]);

  // ── Chemin REPLI : SpeechRecognition navigateur ────────────────────────────
  const demarrer = useCallback(() => {
    const R = moteurVocal();
    if (!R) return;
    setErreur("");
    const r = new R();
    r.lang = "fr-FR";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (ev) => {
      const t = Array.from(ev.results || []).map((x) => x[0]?.transcript || "").join(" ").trim();
      if (t) onTexte(t);
    };
    r.onerror = (ev) => setErreur(ev?.error ? `Dictée interrompue (${ev.error}).` : "Dictée interrompue.");
    r.onend = () => setEcoute(false);
    rec.current = r;
    setEcoute(true);
    try { r.start(); } catch { setEcoute(false); }
  }, [onTexte]);

  return {
    // repli navigateur
    disponible, ecoute, demarrer,
    // principal audio serveur
    audioDispo, enregistre, occupe, demarrerAudio, arreterAudio,
    erreur,
  };
}

export default useDictee;
